import React, { useState, useEffect } from 'react'
import {
  Settings as SettingsIcon,
  Building,
  CreditCard,
  Bell,
  Shield,
  Palette,
  Save,
  Upload,
  X,
  Check,
  Globe,
  DollarSign,
  Clock,
  Mail,
  Phone,
  MapPin,
  AlertCircle
} from 'lucide-react'
import { useApp } from '../context/AppContext'

const tabs = [
  { value: 'business', label: 'Business', icon: Building },
  { value: 'invoicing', label: 'Invoicing', icon: CreditCard },
  { value: 'email', label: 'Email Integration', icon: Mail },
  { value: 'notifications', label: 'Notifications', icon: Bell },
  { value: 'security', label: 'Security', icon: Shield },
]

export default function Settings() {
  const { settings: contextSettings, updateSettings, uploadLogo, deleteLogo, addNotification } = useApp()
  const [settings, setSettings] = useState(contextSettings)
  const [activeTab, setActiveTab] = useState('business')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [logoPreview, setLogoPreview] = useState(settings?.logo)

  // Update local state when context changes (e.g. initial load)
  useEffect(() => {
    if (contextSettings) {
      setSettings(contextSettings)
      setLogoPreview(contextSettings.logo)
    }
  }, [contextSettings])

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (file) {
      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result)
      }
      reader.readAsDataURL(file)

      // Upload to Firebase
      setUploading(true)
      try {
        const url = await uploadLogo(file)
        setSettings(prev => ({ ...prev, logo: url }))
      } catch (error) {
        // Revert preview on error
        setLogoPreview(settings.logo)
      } finally {
        setUploading(false)
      }
    }
  }

  const handleLogoDelete = async () => {
    if (window.confirm('Are you sure you want to remove your logo?')) {
      await deleteLogo()
      setLogoPreview(null)
      setSettings(prev => ({ ...prev, logo: null }))
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateSettings(settings)
    } catch (error) {
      // Notification handled by updateSettings or context
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-600 mt-1">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="card p-2 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeTab === tab.value
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'business' && (
            <BusinessSettings
              settings={settings}
              setSettings={setSettings}
              logoPreview={logoPreview}
              onLogoUpload={handleLogoUpload}
              onLogoDelete={handleLogoDelete}
              uploading={uploading}
            />
          )}

          {activeTab === 'invoicing' && (
            <InvoicingSettings settings={settings} setSettings={setSettings} />
          )}

          {activeTab === 'email' && (
            <EmailSettings settings={settings} setSettings={setSettings} />
          )}

          {activeTab === 'notifications' && (
            <NotificationSettings settings={settings} setSettings={setSettings} />
          )}

          {activeTab === 'security' && (
            <SecuritySettings />
          )}

          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function BusinessSettings({ settings, setSettings, logoPreview, onLogoUpload, onLogoDelete, uploading }) {
  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Business Information</h3>
        <p className="text-sm text-gray-500 mt-1">This information will appear on your invoices</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Business Logo</label>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden relative">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Upload size={32} className="text-gray-400" />
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <label className={`btn btn-secondary cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Upload size={16} />
                {uploading ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={onLogoUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              {logoPreview && (
                <button
                  onClick={onLogoDelete}
                  disabled={uploading}
                  className="btn btn-danger"
                >
                  <X size={16} />
                  Remove Logo
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">PNG, JPG up to 2MB. Recommended: 200x200px</p>
        </div>

        {/* Business Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
          <div className="relative">
            <Building size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={settings.businessName}
              onChange={(e) => handleChange('businessName', e.target.value)}
              className="input pl-10"
              placeholder="Your Company Name"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Email *</label>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              value={settings.businessEmail}
              onChange={(e) => handleChange('businessEmail', e.target.value)}
              className="input pl-10"
              placeholder="contact@yourcompany.com"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
          <div className="relative">
            <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              value={settings.businessPhone}
              onChange={(e) => handleChange('businessPhone', e.target.value)}
              className="input pl-10"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
          <div className="relative">
            <MapPin size={18} className="absolute left-3 top-3 text-gray-400" />
            <textarea
              value={settings.businessAddress}
              onChange={(e) => handleChange('businessAddress', e.target.value)}
              className="input pl-10 pt-2"
              rows={3}
              placeholder="123 Business Street&#10;City, State 12345"
            />
          </div>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Currency</label>
          <div className="relative">
            <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={settings.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              className="input pl-10"
            >
              <option value="GYD">GYD - Guyanese Dollar</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="JPY">JPY - Japanese Yen</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}

function InvoicingSettings({ settings, setSettings }) {
  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Invoice Settings</h3>
          <p className="text-sm text-gray-500 mt-1">Default settings for new invoices</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Invoice Prefix */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number Prefix</label>
            <input
              type="text"
              value={settings.invoicePrefix}
              onChange={(e) => handleChange('invoicePrefix', e.target.value)}
              className="input"
              placeholder="INV-"
            />
            <p className="text-xs text-gray-500 mt-1">Example: INV-2024-0001</p>
          </div>

          {/* Payment Terms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Payment Terms</label>
            <div className="relative">
              <Clock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={settings.paymentTerms}
                onChange={(e) => handleChange('paymentTerms', e.target.value)}
                className="input pl-10"
              >
                <option value="0">Due on Receipt</option>
                <option value="7">Net 7</option>
                <option value="15">Net 15</option>
                <option value="30">Net 30</option>
                <option value="45">Net 45</option>
                <option value="60">Net 60</option>
                <option value="90">Net 90</option>
              </select>
            </div>
          </div>

          {/* Tax Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Tax Rate (%)</label>
              <div className="relative">
                <PercentIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={settings.taxRate}
                  onChange={(e) => handleChange('taxRate', parseFloat(e.target.value) || 0)}
                  className="input pl-10"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tax Label</label>
              <input
                type="text"
                value={settings.taxLabel}
                onChange={(e) => handleChange('taxLabel', e.target.value)}
                className="input"
                placeholder="Tax"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Currency Exchange Display</h3>
          <p className="text-sm text-gray-500 mt-1">Show equivalent totals in another currency on your invoices</p>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Enable Exchange Display</p>
              <p className="text-sm text-gray-500">Show a second currency total (e.g., USD to GYD)</p>
            </div>
            <button
              onClick={() => handleChange('enableExchange', !settings.enableExchange)}
              className={`relative w-12 h-6 rounded-full transition-colors ${settings.enableExchange ? 'bg-primary-600' : 'bg-gray-300'}`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.enableExchange ? 'translate-x-7' : 'translate-x-1'}`}
              />
            </button>
          </div>

          {settings.enableExchange && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-slide-up">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Currency (e.g., GYD)</label>
                <div className="relative">
                  <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={settings.exchangeCurrency || ''}
                    onChange={(e) => handleChange('exchangeCurrency', e.target.value.toUpperCase())}
                    className="input pl-10"
                    placeholder="GYD"
                    maxLength={3}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exchange Rate (1 {settings.currency} = ?)</label>
                <div className="relative">
                  <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    value={settings.exchangeRate || ''}
                    onChange={(e) => handleChange('exchangeRate', parseFloat(e.target.value) || 0)}
                    className="input pl-10"
                    placeholder="210.00"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Payment Integration</h3>
          <p className="text-sm text-gray-500 mt-1">Configure payment methods for your clients</p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <PaymentMethodToggle
              name="Stripe"
              description="Accept credit cards and bank transfers"
              enabled={true}
            />
            <PaymentMethodToggle
              name="PayPal"
              description="Accept PayPal payments"
              enabled={false}
            />
            <PaymentMethodToggle
              name="Bank Transfer"
              description="Show bank details for wire transfers"
              enabled={true}
            />
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Payment integrations require API keys to be configured.
              Visit your payment provider's dashboard to obtain your credentials.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function PaymentMethodToggle({ name, description, enabled }) {
  const [isEnabled, setIsEnabled] = useState(enabled)

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{name}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <button
        onClick={() => setIsEnabled(!isEnabled)}
        className={`relative w-12 h-6 rounded-full transition-colors ${isEnabled ? 'bg-primary-600' : 'bg-gray-300'
          }`}
      >
        <span
          className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-7' : 'translate-x-1'
            }`}
        />
      </button>
    </div>
  )
}

function NotificationSettings({ settings, setSettings }) {
  const handleNotificationChange = (field) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: !prev.notifications[field]
      }
    }))
  }

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Email Notifications</h3>
        <p className="text-sm text-gray-500 mt-1">Choose which notifications you receive</p>
      </div>

      <div className="p-6 space-y-4">
        <NotificationToggle
          title="Invoice Sent"
          description="When you send an invoice to a client"
          checked={settings.notifications.invoiceSent}
          onChange={() => handleNotificationChange('invoiceSent')}
        />
        <NotificationToggle
          title="Invoice Paid"
          description="When a client pays an invoice"
          checked={settings.notifications.invoicePaid}
          onChange={() => handleNotificationChange('invoicePaid')}
        />
        <NotificationToggle
          title="Invoice Overdue"
          description="When an invoice becomes overdue"
          checked={settings.notifications.invoiceOverdue}
          onChange={() => handleNotificationChange('invoiceOverdue')}
        />
        <NotificationToggle
          title="Payment Received"
          description="When a payment is successfully processed"
          checked={settings.notifications.paymentReceived}
          onChange={() => handleNotificationChange('paymentReceived')}
        />
      </div>
    </div>
  )
}

function NotificationToggle({ title, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <button
        onClick={onChange}
        className={`relative w-12 h-6 rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-300'
          }`}
      >
        <span
          className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-7' : 'translate-x-1'
            }`}
        />
      </button>
    </div>
  )
}

function EmailSettings({ settings, setSettings }) {
  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Email Configuration (EmailJS)</h3>
        <p className="text-sm text-gray-500 mt-1">Configure your EmailJS account to send invoices directly.</p>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-blue-50 p-4 rounded-lg flex gap-3 text-blue-800 text-sm">
          <AlertCircle size={20} className="shrink-0" />
          <div>
            <p className="font-semibold mb-1">How to get these keys:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Create a free account at <a href="https://www.emailjs.com" target="_blank" rel="noreferrer" className="underline">emailjs.com</a></li>
              <li>Add an Email Service (e.g., Gmail) to get the <strong>Service ID</strong>.</li>
              <li>Create an Email Template to get the <strong>Template ID</strong>.</li>
              <li>Go to Account fields to find your <strong>Public Key</strong>.</li>
            </ol>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Service ID</label>
          <input
            type="text"
            value={settings.emailServiceId || ''}
            onChange={(e) => handleChange('emailServiceId', e.target.value)}
            className="input"
            placeholder="service_xxxxx"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Template ID</label>
          <input
            type="text"
            value={settings.emailTemplateId || ''}
            onChange={(e) => handleChange('emailTemplateId', e.target.value)}
            className="input"
            placeholder="template_xxxxx"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Public Key</label>
          <input
            type="text"
            value={settings.emailPublicKey || ''}
            onChange={(e) => handleChange('emailPublicKey', e.target.value)}
            className="input"
            placeholder="User Public Key"
          />
        </div>
      </div>
    </div>
  )
}

function SecuritySettings() {
  const [twoFactor, setTwoFactor] = useState(false)

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Password</h3>
          <p className="text-sm text-gray-500 mt-1">Change your account password</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
            <input
              type="password"
              className="input"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              className="input"
              placeholder="Enter new password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              className="input"
              placeholder="Confirm new password"
            />
          </div>
          <button className="btn btn-secondary">Update Password</button>
        </div>
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Two-Factor Authentication</h3>
          <p className="text-sm text-gray-500 mt-1">Add an extra layer of security to your account</p>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Shield size={24} className="text-primary-600" />
              <div>
                <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                <p className="text-sm text-gray-500">Secure your account with 2FA</p>
              </div>
            </div>
            <button
              onClick={() => setTwoFactor(!twoFactor)}
              className={`btn ${twoFactor ? 'btn-danger' : 'btn-primary'}`}
            >
              {twoFactor ? 'Disable' : 'Enable'}
            </button>
          </div>

          {twoFactor && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <Check size={18} />
                <span className="font-medium">Two-factor authentication is enabled</span>
              </div>
              <p className="text-sm text-green-600">
                Your account is now protected with an additional layer of security.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Sessions</h3>
          <p className="text-sm text-gray-500 mt-1">Manage your active sessions</p>
        </div>

        <div className="p-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <Globe size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Current Session</p>
                  <p className="text-sm text-gray-500">Chrome on macOS • San Francisco, CA</p>
                </div>
              </div>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PercentIcon({ size, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  )
}
